import React, {useRef, useState} from 'react'
import { NodeViewWrapper } from '@tiptap/react'

const CarouselComponent = ({ node, updateAttributes, extension, editor }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const images = node.attrs.images || []
  const fileInputRef = useRef(null);
  const { upload } = extension.options;
  const isEditable = editor.isEditable;
  
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return
    
    const uploaded = await Promise.all(files.map(file => upload(file)));
    const newImages = uploaded.map((url, ind) => ({src: url, alt: `image-${ind}`}))
    
    updateAttributes({
      images: [...images, ...newImages],
    })
    
    // Reset so the same file can be re-selected if needed
    e.target.value = '';
  }
  
  const handlePlusClick = () => {
    fileInputRef.current?.click();
  }
  
  const fileInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      multiple
      onChange={handleFileChange}
      style={{ display: 'none' }}
    />
  )

  if (images.length === 0) {
    return (
      <NodeViewWrapper className="image-carousel empty">
        <div className="empty-state">
          {fileInput}
          {/* The visible plus button */}
          <button
            onClick={handlePlusClick}
            className="carousel-add-btn"
            contentEditable={false} // important for Tiptap
          >
            +
          </button>
        </div>
      </NodeViewWrapper>
    )
  }

  const goToSlide = (index) => {
    setCurrentIndex(index)
  }

  const nextSlide = () => {
    setCurrentIndex((currentIndex + 1) % images.length)
  }

  const prevSlide = () => {
    setCurrentIndex((currentIndex - 1 + images.length) % images.length)
  }
  
  const handleDelete = () => {
    const newImages = images.filter((_, i) => i !== currentIndex)
    updateAttributes({ images: newImages })
    setCurrentIndex(prev => Math.min(prev, newImages.length - 1))
  };
  
  const moveSlide = (direction) => {
    const newImageArray = [...images];
    // With this modulo calculation, it will always be within bounds. Swapping at index 0, to the left will give images.length - 1.
    const swapIndex = (((currentIndex + direction) % images.length) + images.length) % images.length;
    
    const temp = newImageArray[swapIndex];
    newImageArray[swapIndex] = newImageArray[currentIndex];
    newImageArray[currentIndex] = temp;
    
    updateAttributes({
      images: [...newImageArray],
    })
    
    setCurrentIndex(swapIndex);
  }

  const currentImage = images[currentIndex]

  // Toolbar shown above the carousel (also in empty state)
  const toolbar = (
    <div className="carousel-toolbar" contentEditable={false}>
      <button
        className="toolbar-btn"
        onClick={() => moveSlide(-1)}
        title="Previous image"
        type="button"
      >
        ‹‹
      </button>
      <button
        className="toolbar-btn"
        onClick={handlePlusClick}
        title="Add image"
        type="button"
      >
        +
      </button>
      <button
        className="toolbar-btn toolbar-btn--delete"
        onClick={handleDelete}
        disabled={images.length === 0}
        title="Remove current image"
        type="button"
      >
        ×
      </button>
      <button
        className="toolbar-btn"
        onClick={() => moveSlide(1)}
        title="Next image"
        type="button"
      >
        ››
      </button>
    </div>
  )

  return (
    <NodeViewWrapper className="image-carousel">
      {isEditable && fileInput}
      <div className="carousel-container">
        {isEditable && toolbar}
        {/* Main image display */}
        <div className="carousel-track">
          <div className="carousel-slide">
            <img
              src={currentImage.src}
              alt={currentImage.alt || ''}
            />
          </div>
        </div>

        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              className="carousel-btn carousel-prev"
              onClick={prevSlide}
              type="button"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M12 4l-8 8 8 8" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
            </button>
            <button
              className="carousel-btn carousel-next"
              onClick={nextSlide}
              type="button"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M8 4l8 8-8 8" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
            </button>
          </>
        )}

        {/* Dots navigation */}
        {images.length > 1 && (
          <div className="carousel-dots">
            {images.map((img, index) => (
              <button
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                type="button"
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}

        {/* Image counter */}
        {images.length > 1 && (
          <div className="carousel-counter">
            {currentIndex + 1} / {images.length}
          </div>
        )}
      </div>
    </NodeViewWrapper>
  )
}

export default CarouselComponent